import React, { useContext, useEffect, useState, useMemo } from 'react';
import {
  Layout,
  Menu,
  Dropdown,
  Avatar,
  Switch,
  Badge,
  Tag,
  Space,
} from 'antd';
import {
  DownOutlined,
  LogoutOutlined,
  ContainerOutlined,
  BellOutlined,
  KeyOutlined,
  LeftOutlined,
} from '@ant-design/icons';
import {
  commonConstants,
  localizationConstants,
  localStorageConstants,
} from 'constants/index';
import { userHelpers, localizationHelpers } from 'helpers';
import { t } from 'helpers/i18n';
import { IRegionItem } from 'interfaces';
import { StoreContext } from 'contexts';
import { useThemeSwitch } from 'hooks/theme';
import { Link } from 'react-router-dom';
import sunIcon from 'assets/images/sun.png';
import moonIcon from 'assets/images/moon.png';
import { useDevice } from 'packages/device/hooks';
import '@terra-js/web-bridge';
import Sp3Module from '@terra-js/sp3-module';
import TerraKit from '@terra-js/terra-kit';

const { Header } = Layout;
const { REGIONS } = localizationConstants;
const { ROLE_TITLE, COMPANY_ID } = localStorageConstants;
const { getCurrentLanguage, changeLanguage } = localizationHelpers;
const { logout } = userHelpers;

const AppHeader: React.FC = () => {
  const params = useMemo(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    return Object.fromEntries(urlSearchParams.entries());
  }, []);

  const { currentUser } = useContext(StoreContext);
  const { device } = useDevice();
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState<
    number
  >();

  const [currentUserRole, setCurrentUserRole] = useState<string>();

  useEffect(() => {
    if (!TerraKit.isAvailable()) {
      return;
    }
    const subscription = Sp3Module.onUnreadNotificationsCountChange().subscribe(
      {
        next: response => {
          setUnreadNotificationsCount(response.result ?? 0);
        },
        complete: () => {},
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const openNotificationList = () => {
    if (!TerraKit.isAvailable()) {
      return;
    }
    Sp3Module.openNotificationList();
  };

  const isShowLinkActivePos365AndTicket = () => {
    if (window.LINK_ACTIVATE_POS365) {
      if (
        localStorage.getItem(COMPANY_ID) ===
          window.COMPANY_ID_VNNG.toString() ||
        localStorage.getItem(COMPANY_ID) ===
          window.COMPANY_ID_DIGILIFE.toString()
      ) {
        return true;
      }
    }
    return false;
  };

  const localizationMenu = (
    <Menu>
      {Object.values(REGIONS).map((el: IRegionItem) => (
        <Menu.Item key={el.key} onClick={() => changeLanguage(el.key)}>
          <Avatar src={el.flag} shape="square" />
          <span style={{ marginLeft: 10 }}>{el.name}</span>
        </Menu.Item>
      ))}
    </Menu>
  );

  const userMenu = (
    <Menu>
      {isShowLinkActivePos365AndTicket() ? (
        <Menu.Item data-testid="btn-link-pos365">
          <KeyOutlined />
          <Link
            to={{ pathname: window.LINK_ACTIVATE_POS365 }}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-base"
          >
            {t('LinkActivePos365')}
          </Link>
        </Menu.Item>
      ) : (
        ''
      )}
      {device === 'mobile' &&
        !TerraKit.isAvailable() &&
        isShowLinkActivePos365AndTicket() && (
          <Menu.Item data-testid="btn-link-roadvn">
            <ContainerOutlined />
            <Link
              to={{ pathname: 'https://road.vnpay.vn/' }}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-base"
            >
              Ticket CSKH
            </Link>
          </Menu.Item>
        )}
      <Menu.Item data-testid="btn-logout" onClick={logout}>
        <LogoutOutlined />
        <span className="ml-base">{t('Logout')}</span>
      </Menu.Item>
    </Menu>
  );

  const currentRegion = REGIONS[getCurrentLanguage()];

  const { isDarkMode, toggleDarkMode } = useThemeSwitch();

  useEffect(() => {
    if (localStorage && localStorage.getItem(ROLE_TITLE)) {
      setCurrentUserRole(localStorage.getItem(ROLE_TITLE) || '');
    }
  }, []);

  if (params.app === commonConstants.APP_DEALER) {
    return (
      <Space
        className={'justify-content-center my-half'}
        style={{ fontSize: '20px' }}
      >
        <LeftOutlined onClick={() => TerraKit.finish()} />
        <div className={'text-bold'} style={{ fontSize: '20px' }}>
          {t('TicketWaiting')}
        </div>
      </Space>
    );
  }

  return (
    <Header
      className="app-header"
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
      }}
    >
      <Switch
        data-testid="theme-switch"
        className="theme-switch mx-base"
        title={t('SwitchTheme')}
        checked={isDarkMode}
        checkedChildren={
          <img width="16" height="16" src={moonIcon} alt="dark" />
        }
        unCheckedChildren={
          <img width="16" height="16" src={sunIcon} alt="light" />
        }
        onClick={toggleDarkMode}
      />
      {device !== 'mobile' && (
        <Dropdown overlay={localizationMenu} trigger={['click']}>
          <span className="app-user">
            <Avatar src={currentRegion && currentRegion.flag} shape="square" />
            <span className="name">{currentRegion && currentRegion.name}</span>
          </span>
        </Dropdown>
      )}

      {TerraKit.isAvailable() && (
        <span
          style={{ marginRight: 10, paddingTop: 5 }}
          onClick={openNotificationList}
        >
          <Badge count={unreadNotificationsCount} overflowCount={10}>
            <BellOutlined style={{ fontSize: 24 }} />
          </Badge>
        </span>
      )}

      <Dropdown overlay={userMenu} trigger={['click']}>
        <span className="app-user">
          <Avatar src={currentUser.picture} />
          <span className="name">
            {currentUser.name} <Tag color="green">{currentUserRole}</Tag>
          </span>
          <DownOutlined />
        </span>
      </Dropdown>
    </Header>
  );
};

export default AppHeader;
